import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IndexTrackerService {
  private lastIndexes: { [key: string]: number } = {};
  private elementCounts: { [key: string]: number } = {};
  private indexChangeSubject = new BehaviorSubject<{ type: string, newIndex: number }>(null);
  private countChangeSubject = new BehaviorSubject<{ type: string, count: number }>(null);

  /** Observable qui émet les changements d'index pour un type donné */
  indexChanges$ = this.indexChangeSubject.asObservable();

  constructor() {
    this.loadFromSessionStorage();
  }

  /**
   * Définit le dernier index utilisé pour un type d'objet donné.
   * @param objectType Type de l'objet (ex: 'PAR', 'ORG')
   * @param index Dernier index utilisé
   */
  setLastIndex(objectType: string, index: number): void {
    this.lastIndexes[objectType] = index;
    this.saveToSessionStorage();
    this.indexChangeSubject.next({ type: objectType, newIndex: index });
  }

  /**
   * Incrémente le compteur d’un type donné et met à jour automatiquement le dernier index.
   * @param objectType Type de l’objet duplicable (ex : 'PAR', 'ORG')
   */
  addDuplicableElement(objectType: string): void {
    const currentCount = this.getElementCount(objectType);
    const newCount = currentCount + 1;

    this.setElementCount(objectType, newCount);
    this.setLastIndex(objectType, newCount);
  }

  /**
   * Décrémente le compteur d’un type donné et met à jour le dernier index selon le type.
   *
   * - Si le type est "PAR" ou "LOT", le dernier index est fixé à 1.
   * - Sinon, il est fixé à -1.
   *
   * @param objectType Type de l’objet duplicable (ex : 'PAR', 'ORG', 'LOC')
   */
  removeDuplicableElement(objectType: string): void {
    const currentCount = this.getElementCount(objectType);
    const newCount = Math.max(0, currentCount - 1);

    this.elementCounts[objectType] = newCount;
    this.lastIndexes[objectType] = (objectType === 'PAR' || objectType === 'LOT') ? 1 : -1;

    this.countChangeSubject.next({ type: objectType, count: newCount });
    this.indexChangeSubject.next({ type: objectType, newIndex: this.lastIndexes[objectType] });

    this.saveToSessionStorage();
  }



  /**
   * Récupère le dernier index utilisé pour un type d'objet donné.
   * @param objectType Type de l'objet
   * @returns Le dernier index ou null s'il n'existe pas
   */
  getLastIndex(objectType: string): number | null {
    return this.lastIndexes[objectType] ?? null;
  }

  /**
   * Met à jour le compteur d'éléments pour un type donné.
   * Si le compteur augmente, met également à jour le dernier index.
   * @param objectType Type de l'objet
   * @param count Nouveau nombre d'éléments
   */
  setElementCount(objectType: string, count: number): void {
    const previousCount = this.elementCounts[objectType] ?? 0;
    this.elementCounts[objectType] = count;

    if (count > previousCount) {
      this.setLastIndex(objectType, count);
    }

    this.saveToSessionStorage();
    this.countChangeSubject.next({ type: objectType, count });
  }

  /**
   * Récupère le nombre actuel d'éléments pour un type donné.
   * @param objectType Type de l'objet
   * @returns Le compteur d'éléments pour ce type
   */
  getElementCount(objectType: string): number {
    return this.elementCounts[objectType] ?? 0;
  }

  /**
   * Réinitialise tous les index et compteurs en mémoire et dans le sessionStorage.
   */
  resetIndexes(): void {
    this.lastIndexes = {};
    this.elementCounts = {};
    sessionStorage.removeItem('lastIndexes');
    sessionStorage.removeItem('elementCounts');
  }

  /**
   * Sauvegarde les index et compteurs actuels dans le sessionStorage.
   */
  private saveToSessionStorage(): void {
    sessionStorage.setItem('lastIndexes', JSON.stringify(this.lastIndexes));
    sessionStorage.setItem('elementCounts', JSON.stringify(this.elementCounts));
  }

  /**
   * Charge les index et compteurs sauvegardés depuis le sessionStorage.
   */
  private loadFromSessionStorage(): void {
    const storedIndexes = sessionStorage.getItem('lastIndexes');
    const storedCounts = sessionStorage.getItem('elementCounts');

    if (storedIndexes) {
      this.lastIndexes = JSON.parse(storedIndexes);
    }
    if (storedCounts) {
      this.elementCounts = JSON.parse(storedCounts);
    }
  }

  /**
   * Met à jour les compteurs d'éléments à partir d'une liste d'entités duplicables.
   * Le type est extrait à partir du préfixe de l'id (ex: 'PAR-123' → 'PAR').
   * Les éléments avec un id null, undefined ou vide sont ignorés.
   * @param duplicableEntities Liste d'objets avec un champ `id` contenant le type
   */
  updateCountsFromList(duplicableEntities: any[]): void {
    const counts = duplicableEntities.reduce((acc, curr) => {
      // Ignorer les éléments sans id valide
      if (!curr || !curr.id || typeof curr.id !== 'string' || curr.id.trim() === '') {
        return acc;
      }
      const type = curr.id.split('-')[0];
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    Object.entries(counts).forEach(([type, count]) => {
      this.setElementCount(type, count as number);
    });
  }
}
